import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { canWrite } from '../../rbac';
import { householdAPI, citizenAPI } from '../../services/api';
import useBackButtonClose from '../../hooks/useBackButtonClose';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ExcelHouseholdTaxImportWorkspace from '../../components/tax/ExcelHouseholdTaxImportWorkspace';
import './Households.css';

const relationOptions = [
  ['self', 'स्वतः'],
  ['spouse', 'पती/पत्नी'],
  ['son', 'मुलगा'],
  ['daughter', 'मुलगी'],
  ['father', 'वडील'],
  ['mother', 'आई'],
  ['other', 'इतर'],
];

const genderOptions = [
  ['male', 'पुरुष'],
  ['female', 'स्त्री'],
  ['other', 'इतर'],
];

const emptyMember = {
  id: '',
  citizen_id: '',
  full_name: '',
  relation_to_head: 'other',
  gender: '',
  date_of_birth: '',
  phone: '',
  email: '',
  aadhaar_number: '',
  occupation: '',
};

const emptyForm = {
  head_citizen_id: '',
  head_name: '',
  head_phone: '',
  head_aadhaar_number: '',
  head_gender: '',
  head_date_of_birth: '',
  ward_number: '',
  address: '',
  house_type: 'pucca',
  income_group: 'APL',
  land_owned_acres: '',
  water_connected: false,
  electricity_meter: '',
  is_verified: false,
  members: [],
};

const normalizeDigits = (value, max) => String(value || '').replace(/\D/g, '').slice(0, max);
const householdCollator = new Intl.Collator('mr-IN', { numeric: true, sensitivity: 'base' });

const sortHouseholdRows = (rows, sort) => [...rows].sort((left, right) => {
  const leftValue = left?.[sort.key];
  const rightValue = right?.[sort.key];
  let result;
  if (typeof leftValue === 'boolean' || typeof rightValue === 'boolean') {
    result = Number(Boolean(leftValue)) - Number(Boolean(rightValue));
  } else if (sort.key === 'total_members') {
    result = Number(leftValue || left?.Citizens?.length || 0) - Number(rightValue || right?.Citizens?.length || 0);
  } else {
    result = householdCollator.compare(String(leftValue ?? ''), String(rightValue ?? ''));
  }
  return sort.direction === 'ASC' ? result : -result;
});

const Field = ({ label, name, value, onChange, type = 'text', required = false, optional = false, isInvalid = false, errorHint, help, ...props }) => (
  <div className="form-group">
    <label htmlFor={name}>
      {label}
      {required && <span className="req-star" aria-hidden="true">*</span>}
      {optional && <span className="opt-badge">(ऐच्छिक)</span>}
    </label>
    <input
      id={name}
      name={name}
      type={type}
      value={value ?? ''}
      onChange={onChange}
      required={required}
      className={isInvalid ? 'invalid-field' : ''}
      {...props}
    />
    {isInvalid && errorHint ? <small className="field-error-hint">⚠ {errorHint}</small> : help ? <small className="property-field-hint">{help}</small> : null}
  </div>
);

const SelectField = ({ label, name, value, onChange, options, required = false, optional = false, isInvalid = false, errorHint, placeholder, disabled = false }) => (
  <div className="form-group">
    <label htmlFor={name}>
      {label}
      {required && <span className="req-star" aria-hidden="true">*</span>}
      {optional && <span className="opt-badge">(ऐच्छिक)</span>}
    </label>
    <select
      id={name}
      name={name}
      value={value ?? ''}
      onChange={onChange}
      required={required}
      disabled={disabled}
      className={isInvalid ? 'invalid-field' : ''}
    >
      {placeholder !== undefined && <option value="">{placeholder}</option>}
      {options.map(option => {
        const item = Array.isArray(option) ? { value: option[0], label: option[1] } : typeof option === 'string' ? { value: option, label: option } : option;
        return <option key={item.value} value={item.value}>{item.label}</option>;
      })}
    </select>
    {isInvalid && errorHint ? <small className="field-error-hint">⚠ {errorHint}</small> : null}
  </div>
);

export default function Households() {
  const { tenant, user } = useAuth();
  const slug = tenant?.slug;
  const canWriteThis = canWrite(user?.role, 'households', user);
  const [households, setHouseholds] = useState([]);
  const [citizenOptions, setCitizenOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showImportView, setShowImportView] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [toast, setToast] = useState({ type: '', text: '' });
  const [invalidFields, setInvalidFields] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [incomeFilter, setIncomeFilter] = useState('');
  const [verificationFilter, setVerificationFilter] = useState('');
  const [pageSize, setPageSize] = useState(20);
  const [sort, setSort] = useState({ key: 'createdAt', direction: 'DESC' });
  const [exporting, setExporting] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [formData, setFormData] = useState(emptyForm);
  const [currentStep, setCurrentStep] = useState(1);
  const [isSinglePageMode, setIsSinglePageMode] = useState(false);

  useBackButtonClose(showForm || showImportView, () => {
    setShowForm(false);
    setShowImportView(false);
    setEditItem(null);
  });

  // Auto-clear floating toast popup message
  useEffect(() => {
    if (!toast.text) return;
    const duration = toast.type === 'success' ? 5000 : 7000;
    const timer = setTimeout(() => setToast({ type: '', text: '' }), duration);
    return () => clearTimeout(timer);
  }, [toast]);

  // Auto scroll smooth to invalid field if validation fails
  useEffect(() => {
    if (!invalidFields.length) return;
    const firstKey = invalidFields[0];
    const element = document.getElementById(firstKey);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      element.focus({ preventScroll: true });
    }
  }, [invalidFields, currentStep]);

  const loadCitizens = useCallback(async () => {
    if (!slug) return;
    try {
      const res = await citizenAPI.getAll(slug, { limit: 200 });
      setCitizenOptions(res.data.citizens || []);
    } catch {
      setCitizenOptions([]);
    }
  }, [slug]);

  const loadData = useCallback(async () => {
    if (!slug) return;
    setLoading(true);
    try {
      const params = { page, limit: pageSize, sort_by: sort.key, sort_order: sort.direction };
      if (debouncedSearch) params.search = debouncedSearch;
      if (incomeFilter) params.income_group = incomeFilter;
      if (verificationFilter) params.is_verified = verificationFilter;
      const res = await householdAPI.getAll(slug, params);
      const nextHouseholds = res.data.households || [];
      const nextTotalPages = res.data.pagination?.totalPages || res.data.totalPages || 1;
      setHouseholds(sortHouseholdRows(nextHouseholds, sort));
      setTotalPages(nextTotalPages);
      setTotalCount(res.data.pagination?.total || 0);
      if (page > nextTotalPages) setPage(nextTotalPages);
    } catch (err) {
      setToast({ type: 'error', text: 'कुटुंबांची माहिती लोड करण्यात अयशस्वी' });
    } finally {
      setLoading(false);
    }
  }, [slug, page, pageSize, debouncedSearch, incomeFilter, verificationFilter, sort]);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => { loadCitizens(); }, [loadCitizens]);
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const steps = [
    { id: 1, title: '१. कुटुंबप्रमुख', sub: 'नागरिक निवड किंवा नवीन नोंद' },
    { id: 2, title: '२. घराची माहिती', sub: 'प्रभाग, पत्ता, उत्पन्न व जोडणी' },
    { id: 3, title: '३. कुटुंबातील सदस्य', sub: 'सदस्यांची नावे व नातेसंबंध' },
  ];

  const handleSearchChange = (val) => { setSearchInput(val); };
  const handleIncomeFilterChange = (val) => { setIncomeFilter(val); setPage(1); };
  const toggleSort = (key) => {
    setSort(current => ({ key, direction: current.key === key && current.direction === 'ASC' ? 'DESC' : 'ASC' }));
    setPage(1);
  };

  const exportCsv = async () => {
    if (!slug || exporting) return;
    setExporting(true); setToast({ type: '', text: '' });
    try {
      const params = { page: 1, limit: Math.max(totalCount, pageSize), sort_by: sort.key, sort_order: sort.direction };
      if (debouncedSearch) params.search = debouncedSearch;
      if (incomeFilter) params.income_group = incomeFilter;
      if (verificationFilter) params.is_verified = verificationFilter;
      const { data } = await householdAPI.getAll(slug, params);
      const rows = data.households || [];
      const csvCell = value => `"${String(value ?? '').replace(/"/g, '""')}"`;
      const csv = [
        ['UID', 'कुटुंबप्रमुख', 'पत्ता', 'प्रभाग', 'घर प्रकार', 'उत्पन्न गट', 'सदस्य', 'स्थिती'],
        ...rows.map(h => [h.household_uid, h.head_name, h.address, h.ward_number, h.house_type, h.income_group, h.total_members || h.Citizens?.length || 0, h.is_verified ? 'पडताळलेले' : 'प्रलंबित']),
      ].map(row => row.map(csvCell).join(',')).join('\n');
      const url = URL.createObjectURL(new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8' }));
      const link = document.createElement('a'); link.href = url; link.download = `households-${new Date().toISOString().slice(0, 10)}.csv`; link.click();
      URL.revokeObjectURL(url);
    } catch (err) { setToast({ type: 'error', text: 'निर्यात तयार करता आली नाही.' }); }
    finally { setExporting(false); }
  };

  const openCreate = () => {
    setShowForm(!showForm);
    setShowImportView(false);
    setEditItem(null);
    setFormData(emptyForm);
    setCurrentStep(1);
    setInvalidFields([]);
  };

  const validateStep1 = useCallback(() => {
    const missing = [];
    if (!formData.head_citizen_id && !formData.head_name.trim()) missing.push('head_name');
    if (!formData.head_citizen_id && formData.head_phone && formData.head_phone.length !== 10) missing.push('head_phone');
    if (!formData.head_citizen_id && formData.head_aadhaar_number && formData.head_aadhaar_number.length !== 12) missing.push('head_aadhaar_number');
    return {
      isValid: missing.length === 0,
      missingFields: missing,
      errorText: missing.length > 0 ? (formData.head_phone && formData.head_phone.length !== 10 ? 'मोबाईल नंबर १० अंकांचा असावा.' : formData.head_aadhaar_number && formData.head_aadhaar_number.length !== 12 ? 'आधार क्रमांक १२ अंकांचा असावा.' : 'कृपया कुटुंबप्रमुख निवडा किंवा नवीन नाव भरा.') : ''
    };
  }, [formData.head_citizen_id, formData.head_name, formData.head_phone, formData.head_aadhaar_number]);

  const validateStep2 = useCallback(() => {
    const missing = [];
    if (!formData.address || !formData.address.trim()) missing.push('address');
    return {
      isValid: missing.length === 0,
      missingFields: missing,
      errorText: missing.length > 0 ? 'कृपया घराचा पूर्ण पत्ता टाका.' : ''
    };
  }, [formData.address]);

  const validateStep3 = useCallback(() => {
    const missing = [];
    const usedPhones = new Set();
    if (formData.head_phone && formData.head_phone.length === 10) {
      usedPhones.add(formData.head_phone);
    }
    let duplicatePhoneFound = false;

    formData.members.forEach((member, index) => {
      if (member.full_name?.trim()) {
        if (member.phone && member.phone.length !== 10) missing.push(`member-phone-${index}`);
        if (member.aadhaar_number && member.aadhaar_number.length !== 12) missing.push(`member-aadhaar-${index}`);
        if (member.phone && member.phone.length === 10) {
          if (usedPhones.has(member.phone)) {
            missing.push(`member-phone-${index}`);
            duplicatePhoneFound = true;
          } else {
            usedPhones.add(member.phone);
          }
        }
      }
    });
    let errorText = '';
    if (missing.length > 0) {
      if (duplicatePhoneFound) errorText = 'फॉर्ममध्ये समान मोबाईल नंबर दोनदा वापरला आहे.';
      else errorText = 'सदस्यांचा मोबाईल १० अंकांचा व आधार १२ अंकांचा असावा.';
    }
    return {
      isValid: missing.length === 0,
      missingFields: missing,
      errorText,
    };
  }, [formData.members, formData.head_phone]);

  const checkStepValidity = useCallback((stepNo) => {
    if (stepNo === 1) return validateStep1();
    if (stepNo === 2) return validateStep2();
    if (stepNo === 3) return validateStep3();
    return { isValid: true, missingFields: [], errorText: '' };
  }, [validateStep1, validateStep2, validateStep3]);

  const handleStepClick = (targetStep) => {
    if (targetStep <= currentStep) {
      setCurrentStep(targetStep);
      return;
    }
    for (let s = 1; s < targetStep; s++) {
      const check = checkStepValidity(s);
      if (!check.isValid) {
        setInvalidFields(check.missingFields);
        setToast({ type: 'error', text: `पायरी ${s} अपूर्ण आहे: ${check.errorText}` });
        setCurrentStep(s);
        return;
      }
    }
    setInvalidFields([]);
    setToast({ type: '', text: '' });
    setCurrentStep(targetStep);
  };

  const handleNextStep = () => {
    const check = checkStepValidity(currentStep);
    if (!check.isValid) {
      setInvalidFields(check.missingFields);
      return setToast({ type: 'error', text: check.errorText });
    }
    setInvalidFields([]);
    setToast({ type: '', text: '' });
    setCurrentStep(prev => Math.min(steps.length, prev + 1));
  };

  const buildPayload = () => ({
    ...formData,
    head_aadhaar_number: formData.head_citizen_id ? undefined : (normalizeDigits(formData.head_aadhaar_number, 12) || undefined),
    head_phone: formData.head_citizen_id ? undefined : (normalizeDigits(formData.head_phone, 10) || undefined),
    members: formData.members
      .filter(member => member.full_name?.trim())
      .map(member => ({
        ...member,
        id: member.citizen_id || member.id || undefined,
        aadhaar_number: normalizeDigits(member.aadhaar_number, 12) || undefined,
        phone: normalizeDigits(member.phone, 10) || undefined,
        email: member.email?.trim() || undefined,
      })),
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    for (let s = 1; s <= 3; s++) {
      const check = checkStepValidity(s);
      if (!check.isValid) {
        setInvalidFields(check.missingFields);
        setCurrentStep(s);
        return setToast({ type: 'error', text: `पायरी ${s} मधील त्रुटी: ${check.errorText}` });
      }
    }

    setSaving(true);
    setToast({ type: '', text: '' });

    try {
      const payload = buildPayload();
      if (editItem) {
        await householdAPI.update(slug, editItem.id, payload);
        setToast({ type: 'success', text: 'कुटुंब आणि सदस्य माहिती अद्ययावत केली' });
      } else {
        await householdAPI.create(slug, payload);
        setToast({ type: 'success', text: 'कुटुंब आणि नागरिक नोंदी तयार केल्या' });
      }
      setShowForm(false);
      setEditItem(null);
      setFormData(emptyForm);
      await Promise.all([loadData(), loadCitizens()]);
    } catch (err) {
      setToast({ type: 'error', text: err.response?.data?.error || 'कुटुंब जतन करण्यात अयशस्वी' });
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (item) => {
    const headCitizen = item.Citizens?.find(c => c.relation_to_head === 'self');
    const members = (item.Citizens || [])
      .filter(c => c.id !== headCitizen?.id)
      .map(c => ({
        ...emptyMember,
        id: c.id,
        citizen_id: c.id,
        full_name: c.full_name || '',
        relation_to_head: c.relation_to_head || 'other',
        gender: c.gender || '',
        date_of_birth: c.date_of_birth || '',
        phone: c.phone || '',
        email: c.email || '',
        aadhaar_number: c.aadhaar_number || '',
        occupation: c.occupation || '',
      }));

    setEditItem(item);
    setFormData({
      ...emptyForm,
      head_citizen_id: headCitizen?.id || '',
      head_name: headCitizen?.full_name || item.head_name || '',
      head_phone: headCitizen?.phone || '',
      head_aadhaar_number: headCitizen?.aadhaar_number || '',
      head_gender: headCitizen?.gender || '',
      head_date_of_birth: headCitizen?.date_of_birth || '',
      ward_number: item.ward_number || '',
      address: item.address || '',
      house_type: item.house_type || 'pucca',
      income_group: item.income_group || 'APL',
      land_owned_acres: item.land_owned_acres || '',
      water_connected: !!item.water_connected,
      electricity_meter: item.electricity_meter || '',
      is_verified: !!item.is_verified,
      members,
    });
    setShowForm(true);
    setCurrentStep(1);
    setInvalidFields([]);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setInvalidFields(current => current.filter(item => item !== name));

    if (name === 'head_citizen_id') {
      const selectedCitizen = citizenOptions.find(c => String(c.id) === String(value));
      setFormData(prev => ({
        ...prev,
        head_citizen_id: value,
        head_name: selectedCitizen?.full_name || '',
        head_phone: selectedCitizen?.phone || '',
        head_aadhaar_number: selectedCitizen?.aadhaar_number || '',
        head_gender: selectedCitizen?.gender || '',
        head_date_of_birth: selectedCitizen?.date_of_birth || '',
      }));
      return;
    }
    if (name === 'head_phone') {
      setFormData(prev => ({ ...prev, [name]: normalizeDigits(value, 10) }));
      return;
    }
    if (name === 'head_aadhaar_number') {
      setFormData(prev => ({ ...prev, [name]: normalizeDigits(value, 12) }));
      return;
    }
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const updateMember = (index, field, value) => {
    setInvalidFields(current => current.filter(item => item !== `member-${field}-${index}`));
    
    if (field === 'citizen_id') {
      const selectedCitizen = citizenOptions.find(c => String(c.id) === String(value));
      setFormData(prev => ({
        ...prev,
        members: prev.members.map((member, i) => (
          i === index ? {
            ...member,
            id: value || '',
            citizen_id: value || '',
            full_name: selectedCitizen?.full_name || (value ? member.full_name : ''),
            gender: selectedCitizen?.gender || (value ? member.gender : ''),
            date_of_birth: selectedCitizen?.date_of_birth || (value ? member.date_of_birth : ''),
            phone: selectedCitizen?.phone || (value ? member.phone : ''),
            email: selectedCitizen?.email || (value ? member.email : ''),
            aadhaar_number: selectedCitizen?.aadhaar_number || (value ? member.aadhaar_number : ''),
            occupation: selectedCitizen?.occupation || (value ? member.occupation : ''),
          } : member
        )),
      }));
      return;
    }

    const nextValue = field === 'phone'
      ? normalizeDigits(value, 10)
      : field === 'aadhaar_number'
        ? normalizeDigits(value, 12)
        : value;

    setFormData(prev => ({
      ...prev,
      members: prev.members.map((member, i) => (
        i === index ? { ...member, [field]: nextValue } : member
      )),
    }));
  };

  const addMember = () => {
    setFormData(prev => ({ ...prev, members: [...prev.members, { ...emptyMember }] }));
  };

  const removeMember = (index) => {
    setFormData(prev => ({ ...prev, members: prev.members.filter((_, i) => i !== index) }));
  };

  return (
    <div>
      {/* Global Floating Toast Popup */}
      {toast.text && (
        <div className="gp-toast-container">
          <div className={`gp-toast gp-toast-${toast.type || 'info'}`} role="alert">
            <span className="gp-toast-icon">
              {toast.type === 'success' ? '✓' : toast.type === 'error' ? '⚠' : 'ℹ'}
            </span>
            <div className="gp-toast-content">
              <div className="gp-toast-title">
                {toast.type === 'success' ? 'यशस्वी क्रिया' : toast.type === 'error' ? 'फॉर्म अपूर्ण / त्रुटी' : 'माहिती'}
              </div>
              <div className="gp-toast-message">{toast.text}</div>
            </div>
            <button
              type="button"
              className="gp-toast-close"
              onClick={() => setToast({ type: '', text: '' })}
              title="बंद करा"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      <div className="admin-card-header">
        <h2>🏠 कुटुंब व्यवस्थापन</h2>
        <div style={{ display: 'flex', gap: '0.65rem', alignItems: 'center' }}>
          {canWriteThis && !showImportView && (
            <button
              type="button"
              className="btn"
              onClick={() => {
                setShowImportView(true);
                setShowForm(false);
                setEditItem(null);
              }}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.4rem',
                background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                color: '#fff',
                border: 'none',
                borderRadius: '0.5rem',
                padding: '0.55rem 0.95rem',
                fontWeight: 700,
                fontSize: '0.875rem',
                cursor: 'pointer',
                boxShadow: '0 2px 6px rgba(5, 150, 105, 0.25)',
              }}
              title="एक्सेल द्वारे घरे, नागरिक, मालमत्ता व कर मागण्या एकत्रित आयात करा"
            >
              📊 एक्सेल आयात
            </button>
          )}
          {canWriteThis && !showImportView && (
            <button className="btn household-add-btn" onClick={openCreate}>
              {showForm ? 'रद्द करा' : '+ कुटुंब जोडा'}
            </button>
          )}
        </div>
      </div>

      {showImportView && (
        <ExcelHouseholdTaxImportWorkspace
          tenantSlug={slug}
          onBack={() => setShowImportView(false)}
          onImportSuccess={() => {
            loadData();
            setToast({ type: 'success', text: 'एक्सेल आयात यशस्वीरित्या पूर्ण झाली! सर्व कुटुंबे अद्ययावत झाली आहेत.' });
          }}
        />
      )}

      {!showImportView && showForm && (
        <div className="admin-card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
            <div>
              <h2>{editItem ? 'कुटुंब संपादित करा' : 'नवीन कुटुंब व नागरिक नोंदणी'}</h2>
              <p style={{ marginTop: 2, color: '#64748b', fontSize: '0.88rem' }}>
                कुटुंबप्रमुख आणि सदस्य यांची माहिती येथे भरल्यावर नागरिक नोंदी आपोआप लिंक होतील. नंतर हेच नागरिक लॉगिन करून KYC व कर भरणा करू शकतील.
              </p>
            </div>

            <button
              type="button"
              className="household-stepper-toggle"
              onClick={() => setIsSinglePageMode(!isSinglePageMode)}
            >
              {isSinglePageMode ? '🧩 बहु-पायरी फॉर्मवर जा (Multi-Step Wizard)' : '📄 सर्व विभाग एकत्र पहा (Full Page)'}
            </button>
          </div>

          {/* Stepper Header */}
          {!isSinglePageMode && (
            <div className="household-stepper-container" style={{ marginTop: 16 }}>
              <div className="household-stepper-header">
                <span>पायरी {currentStep} / {steps.length}: <strong>{steps[currentStep - 1]?.title}</strong></span>
              </div>
              <div className="household-stepper">
                {steps.map((step, idx) => {
                  const stepNum = idx + 1;
                  const isActive = currentStep === stepNum;
                  const isCompleted = currentStep > stepNum;
                  const isLocked = stepNum > currentStep && !checkStepValidity(stepNum - 1).isValid;
                  return (
                    <button
                      key={step.id}
                      type="button"
                      className={`household-stepper-tab ${isActive ? 'active' : ''} ${isCompleted ? 'completed' : ''} ${isLocked ? 'restricted' : ''}`}
                      onClick={() => handleStepClick(stepNum)}
                      title={isLocked ? `पायरी ${stepNum - 1} अपूर्ण आहे` : step.title}
                    >
                      <span className="household-step-num">{isCompleted ? '✓' : isLocked ? '🔒' : stepNum}</span>
                      <div className="household-step-label">
                        <strong>{step.title}</strong>
                        <small>{step.sub}</small>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate>
            {/* Step 1: कुटुंबप्रमुख */}
            {(isSinglePageMode || currentStep === 1) && (
              <section className="property-form-card" style={{ padding: 18, marginTop: 16 }}>
                <h3 style={{ margin: '0 0 14px', color: '#173d5d', fontSize: '1.05rem' }}>१. कुटुंबप्रमुख माहिती</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 15 }}>
                  <SelectField
                    label="अस्तित्वातील नागरिक निवडा"
                    name="head_citizen_id"
                    value={formData.head_citizen_id}
                    onChange={handleChange}
                    placeholder="नवीन नागरिक म्हणून तयार करा"
                    options={citizenOptions.map(c => [c.id, `${c.full_name} ${c.phone ? `(${c.phone})` : ''}`])}
                    optional
                  />
                  <Field
                    label="नवीन कुटुंबप्रमुखाचे नाव"
                    name="head_name"
                    value={formData.head_name}
                    onChange={handleChange}
                    disabled={!!formData.head_citizen_id}
                    required={!formData.head_citizen_id}
                    optional={!!formData.head_citizen_id}
                    isInvalid={invalidFields.includes('head_name')}
                    errorHint="कुटुंबप्रमुखाचे नाव भरले पाहिजे."
                  />
                  <Field
                    label="मोबाईल"
                    name="head_phone"
                    value={formData.head_phone}
                    onChange={handleChange}
                    disabled={!!formData.head_citizen_id}
                    maxLength={10}
                    optional
                    isInvalid={invalidFields.includes('head_phone')}
                    errorHint="मोबाईल नंबर १० अंकांचा असावा."
                    inputMode="tel"
                  />
                  <Field
                    label="आधार क्रमांक"
                    name="head_aadhaar_number"
                    value={formData.head_aadhaar_number}
                    onChange={handleChange}
                    disabled={!!formData.head_citizen_id}
                    maxLength={12}
                    optional
                    isInvalid={invalidFields.includes('head_aadhaar_number')}
                    errorHint="आधार क्रमांक १२ अंकांचा असावा."
                    inputMode="numeric"
                  />
                  <SelectField
                    label="लिंग"
                    name="head_gender"
                    value={formData.head_gender}
                    onChange={handleChange}
                    disabled={!!formData.head_citizen_id}
                    options={genderOptions}
                    placeholder="निवडा"
                    optional
                  />
                  <Field
                    label="जन्मतारीख"
                    name="head_date_of_birth"
                    type="date"
                    value={formData.head_date_of_birth}
                    onChange={handleChange}
                    disabled={!!formData.head_citizen_id}
                    optional
                  />
                </div>
              </section>
            )}

            {/* Step 2: घराची माहिती */}
            {(isSinglePageMode || currentStep === 2) && (
              <section className="property-form-card" style={{ padding: 18, marginTop: 16 }}>
                <h3 style={{ margin: '0 0 14px', color: '#173d5d', fontSize: '1.05rem' }}>२. घराची माहिती व सुविधा</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 15 }}>
                  <Field
                    label="प्रभाग"
                    name="ward_number"
                    value={formData.ward_number}
                    onChange={handleChange}
                    optional
                  />
                  <SelectField
                    label="घराचा प्रकार"
                    name="house_type"
                    value={formData.house_type}
                    onChange={handleChange}
                    options={[['pucca', 'पक्के'], ['semi_pucca', 'अर्ध पक्के'], ['kutcha', 'कच्चे']]}
                    required
                  />
                  <div style={{ gridColumn: 'span 2' }}>
                    <Field
                      label="पत्ता"
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      required
                      isInvalid={invalidFields.includes('address')}
                      errorHint="घराचा पत्ता टाकणे आवश्यक आहे."
                    />
                  </div>
                  <SelectField
                    label="उत्पन्न गट"
                    name="income_group"
                    value={formData.income_group}
                    onChange={handleChange}
                    options={[['APL', 'APL'], ['BPL', 'BPL'], ['EWS', 'EWS']]}
                    required
                  />
                  <Field
                    label="जमीन (एकर)"
                    name="land_owned_acres"
                    type="number"
                    step="0.01"
                    value={formData.land_owned_acres}
                    onChange={handleChange}
                    optional
                  />
                  <Field
                    label="वीज मीटर क्रमांक"
                    name="electricity_meter"
                    value={formData.electricity_meter}
                    onChange={handleChange}
                    optional
                  />
                  <div className="form-group" style={{ display: 'flex', gap: 24, alignItems: 'center', paddingTop: 24 }}>
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontWeight: 650, color: '#3d566b' }}>
                      <input type="checkbox" name="water_connected" checked={formData.water_connected} onChange={handleChange} style={{ width: 18, height: 18 }} /> पाणी जोडणी
                    </label>
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontWeight: 650, color: '#3d566b' }}>
                      <input type="checkbox" name="is_verified" checked={formData.is_verified} onChange={handleChange} style={{ width: 18, height: 18 }} /> पडताळलेले
                    </label>
                  </div>
                </div>
              </section>
            )}

            {/* Step 3: कुटुंबातील सदस्य */}
            {(isSinglePageMode || currentStep === 3) && (
              <section className="property-form-card" style={{ padding: 18, marginTop: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                  <h3 style={{ margin: 0, color: '#173d5d', fontSize: '1.05rem' }}>३. कुटुंबातील इतर सदस्य</h3>
                  <button type="button" className="btn btn-outline btn-sm" onClick={addMember}>+ सदस्य जोडा</button>
                </div>

                {formData.members.length === 0 && (
                  <div className="alert" style={{ background: '#f8fafc', marginTop: 12 }}>
                    कुटुंबप्रमुखाव्यतिरिक्त इतर सदस्य असल्यास <strong>+ सदस्य जोडा</strong> बटणावर क्लिक करा.
                  </div>
                )}

                {formData.members.map((member, index) => (
                  <div key={index} className="admin-card" style={{ padding: 14, marginTop: 12, boxShadow: 'none', border: '1px solid #dce6ee' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
                      <strong>सदस्य {index + 1}</strong>
                      <button type="button" className="btn btn-outline btn-sm" onClick={() => removeMember(index)}>काढा</button>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 12, marginTop: 12 }}>
                      <div style={{ gridColumn: 'span 3' }}>
                        <SelectField
                          label="नागरिक निवडा किंवा नवीन नोंद"
                          name={`member-citizen-id-${index}`}
                          value={member.citizen_id || member.id || ''}
                          onChange={e => updateMember(index, 'citizen_id', e.target.value)}
                          options={citizenOptions
                            .filter(c => String(c.id) !== String(formData.head_citizen_id))
                            .map(c => ({
                              value: String(c.id),
                              label: `${c.full_name}${c.phone ? ` • ${c.phone}` : ''}${c.aadhaar_number ? ` • ${c.aadhaar_number}` : ''}`,
                            }))}
                          placeholder="-- नवीन नोंद तयार करा (Create New Member) --"
                          optional
                        />
                      </div>
                      <Field
                        label="पूर्ण नाव"
                        name={`member-name-${index}`}
                        value={member.full_name}
                        onChange={e => updateMember(index, 'full_name', e.target.value)}
                        optional
                      />
                      <SelectField
                        label="नाते"
                        name={`member-relation-${index}`}
                        value={member.relation_to_head}
                        onChange={e => updateMember(index, 'relation_to_head', e.target.value)}
                        options={relationOptions.filter(([value]) => value !== 'self')}
                        optional
                      />
                      <SelectField
                        label="लिंग"
                        name={`member-gender-${index}`}
                        value={member.gender}
                        onChange={e => updateMember(index, 'gender', e.target.value)}
                        options={genderOptions}
                        placeholder="निवडा"
                        optional
                      />
                      <Field
                        label="जन्मतारीख"
                        name={`member-dob-${index}`}
                        type="date"
                        value={member.date_of_birth}
                        onChange={e => updateMember(index, 'date_of_birth', e.target.value)}
                        optional
                      />
                      <Field
                        label="मोबाईल"
                        name={`member-phone-${index}`}
                        value={member.phone}
                        maxLength={10}
                        onChange={e => updateMember(index, 'phone', e.target.value)}
                        optional
                        isInvalid={invalidFields.includes(`member-phone-${index}`)}
                        errorHint="१० अंकी नंबर"
                        inputMode="tel"
                      />
                      <Field
                        label="आधार क्रमांक"
                        name={`member-aadhaar-${index}`}
                        value={member.aadhaar_number}
                        maxLength={12}
                        onChange={e => updateMember(index, 'aadhaar_number', e.target.value)}
                        optional
                        isInvalid={invalidFields.includes(`member-aadhaar-${index}`)}
                        errorHint="१२ अंकी आधार"
                        inputMode="numeric"
                      />
                      <div style={{ gridColumn: 'span 3' }}>
                        <Field
                          label="व्यवसाय"
                          name={`member-occupation-${index}`}
                          value={member.occupation}
                          onChange={e => updateMember(index, 'occupation', e.target.value)}
                          optional
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </section>
            )}

            {/* Stepper Action Bar */}
            {!isSinglePageMode && (
              <div className="household-step-actions">
                <button
                  type="button"
                  className="btn-prev"
                  disabled={currentStep === 1}
                  onClick={() => setCurrentStep(prev => Math.max(1, prev - 1))}
                >
                  ← मागील पायरी
                </button>

                <button
                  type="submit"
                  className="btn-save"
                  disabled={saving}
                >
                  {saving ? <><span className="btn-spinner"></span> जतन होत आहे…</> : editItem ? 'अद्ययावत करा' : 'मसुदा जतन करा'}
                </button>

                {currentStep < steps.length ? (
                  <button
                    type="button"
                    className="btn-next"
                    onClick={handleNextStep}
                  >
                    पुढील पायरी →
                  </button>
                ) : null}
              </div>
            )}

            {isSinglePageMode && (
              <div className="admin-form-actions" style={{ marginTop: 18, display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? <><span className="btn-spinner"></span> जतन होत आहे…</> : editItem ? 'अद्ययावत करा' : 'कुटुंब तयार करा'}
                </button>
                <button type="button" className="btn btn-outline" onClick={() => { setShowForm(false); setEditItem(null); setFormData(emptyForm); }}>रद्द करा</button>
              </div>
            )}
          </form>
        </div>
      )}

      {!showImportView && !showForm && (
        <div className="admin-card">
          <>
            <div className="household-table-toolbar">
              <div className="household-search"><span>⌕</span><input placeholder="कुटुंबप्रमुख, UID किंवा पत्त्याने शोधा..." value={searchInput} onChange={e => handleSearchChange(e.target.value)} /></div>
              <select value={incomeFilter} onChange={e => handleIncomeFilterChange(e.target.value)}><option value="">सर्व उत्पन्न गट</option><option value="BPL">BPL</option><option value="APL">APL</option><option value="EWS">EWS</option></select>
              <select value={verificationFilter} onChange={e => { setVerificationFilter(e.target.value); setPage(1); }}><option value="">सर्व स्थिती</option><option value="true">पडताळलेले</option><option value="false">प्रलंबित</option></select>
              <button className="household-export-btn" onClick={exportCsv} disabled={exporting}>⇩ {exporting ? 'तयार होत आहे...' : 'CSV निर्यात'}</button>
            </div>
            <div className="household-table-meta"><span><strong>{totalCount}</strong> कुटुंब नोंदी</span><span className={`household-load-status ${loading ? 'visible' : ''}`}>{loading && <><i /> माहिती अपडेट होत आहे</>}</span></div>
            <div className="table-scroll household-table-scroll">
              <table className="data-table household-data-table">
                <thead>
                  <tr>
                    <SortHeader label="UID" column="household_uid" sort={sort} onSort={toggleSort} />
                    <SortHeader label="कुटुंबप्रमुख" column="head_name" sort={sort} onSort={toggleSort} />
                    <SortHeader label="प्रभाग" column="ward_number" sort={sort} onSort={toggleSort} />
                    <SortHeader label="घर प्रकार" column="house_type" sort={sort} onSort={toggleSort} />
                    <SortHeader label="उत्पन्न" column="income_group" sort={sort} onSort={toggleSort} />
                    <SortHeader label="सदस्य" column="total_members" sort={sort} onSort={toggleSort} />
                    <SortHeader label="स्थिती" column="is_verified" sort={sort} onSort={toggleSort} />
                    <th>कृती</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && households.length === 0 ? (
                    <LoadingSpinner.TableRow colSpan={8} message="कुटुंब माहिती लोड होत आहे..." />
                  ) : households.length === 0 ? (
                    <tr>
                      <td colSpan="8">कुटुंब नोंदी आढळली नाही.</td>
                    </tr>
                  ) : households.map((h) => (
                    <tr key={h.id}>
                      <td><span className="household-uid">{h.household_uid}</span></td>
                      <td><div className="household-head-cell"><span>{(h.head_name || '?').charAt(0)}</span><div><strong>{h.head_name}</strong><small>{h.Citizens?.find(c => c.relation_to_head === 'self')?.phone || h.address || 'संपर्क उपलब्ध नाही'}</small></div></div></td>
                      <td>{h.ward_number || '-'}</td>
                      <td><span className="house-type-label">{({ pucca: 'पक्के', semi_pucca: 'अर्ध पक्के', kutcha: 'कच्चे' })[h.house_type] || h.house_type || '-'}</span></td>
                      <td><span className={`income-pill income-${String(h.income_group || '').toLowerCase()}`}>{h.income_group || '-'}</span></td>
                      <td>{h.total_members || h.Citizens?.length || 0}</td>
                      <td>{h.is_verified ? <span className="verify-pill verified">✓ पडताळलेले</span> : <span className="verify-pill pending">◷ प्रलंबित</span>}</td>
                      <td>{canWriteThis && <button className="household-edit-btn" onClick={() => handleEdit(h)}>संपादित करा <span>›</span></button>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="table-pagination household-pagination">
              <label>प्रति पृष्ठ <select value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}><option value="10">10</option><option value="20">20</option><option value="50">50</option></select></label>
              <button className="btn btn-outline btn-sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>← मागील</button>
              <span>पृष्ठ <strong>{page}</strong> / {totalPages}</span>
              <button className="btn btn-outline btn-sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}>पुढील →</button>
            </div>
          </>
        </div>
      )}
    </div>
  );
}

function SortHeader({ label, column, sort, onSort }) {
  const active = sort.key === column;
  return <th><button className={`sort-header ${active ? 'active' : ''}`} onClick={() => onSort(column)}>{label}<span>{active ? (sort.direction === 'ASC' ? '↑' : '↓') : '↕'}</span></button></th>;
}
